import Link from 'next/link';
import { NextSeo } from 'next-seo';
import MainLayout from 'components/layout/MainLayout';
import { ASSET_PREFIX } from 'utils/constants';
import PageTitle from 'components/PageTitle';
import AboutUsModule from 'components/about-us/AboutUsModule';
import RoundedButton, { ColorStyle } from 'components/RoundedButton';
import FaqCard from 'components/FaqCard';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import styles from './how-it-works.module.css';
import { Fragment, ReactNode, useState } from 'react';
import TabControl from 'components/tab-control/TabControl';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import {
  prizeTableByPlanType,
  IPrizeTableEntry,
  getMatchText,
} from 'components/how-it-works/prizeTableData';
import GenericModal from 'components/GenericModal';
import EasySteps from 'components/how-it-works/EasySteps';
import Arrow10x20 from 'components/svg/Arrow10x20';

const faqs: [question: string, answer: ReactNode][] = [
  [
    'How do I enter for a specific prize?',
    <>
      <p>
        If you play our weekly draw, you play for all prizes on our site! With
        one entry, you can win prizes big and small. Once you win in a certain
        category, you get to choose the prize you like from within that prize
        category. The more numbers you match, the higher the value of your
        prize.
      </p>
      <p>
        The only prize category on our site that you aren't automatically
        entered into is the DAYMAKERboost. To win prizes in this category,
        simply select 'boost' when subscribing to our draws.
      </p>
    </>,
  ],
  [
    'How can I see my entries?',
    <p>
      If you've already signed up, you can find{' '}
      <Link href="/account/entries">your entries here</Link>. If you're a
      subscriber but can't see any entries, please email our team{' '}
      <a href="mailto:hello@daymade.co.uk">hello@daymade.co.uk</a> who can help
      you locate them!
    </p>,
  ],
  [
    'Can I change my lucky numbers?',
    <p>
      Yes, you can change your{' '}
      <Link href="account/subscriptions">lucky numbers right here</Link>. When
      changing your numbers, the change will take effect after the upcoming
      draw.
    </p>,
  ],
  [
    'Is it easy to cancel my subscription?',
    <p>
      Yes, you can cancel your subscription at any time. Simply go to My
      Account, click '
      <Link href="account/subscriptions">Manage Subscription</Link>' and then
      click 'Cancel Subscription'.
    </p>,
  ],
  [
    'How are winning numbers selected?',
    <p>
      On Fridays, we draw the winning numbers from a Random Number Generator
      live on Instagram (
      <a target="_blank" href="https://www.instagram.com/daymadeuk/">
        @daymadeuk
      </a>
      ). So as a player, you can be a witness to see how the winning numbers are
      selected! On Tuesdays, we draw the winning numbers automatically through
      Random.org, a third-party trusted random number generator.
    </p>,
  ],
  [
    'Do people actually win?',
    <p>
      Yes, we have thousands of weekly winners winning prizes big and small. Due
      to the nature of our game, most winners will win smaller prizes such as
      tokens, free entries and bronze prizes. That said, every week we also have
      several winners winning dream trips and big prizes like gadget bundles or
      a new furniture haul. It's all the luck of the draw!
    </p>,
  ],
  [
    'What are DAYMADE Tokens?',
    <>
      <p>
        Think of Tokens like DAYMADE credit, which can be collected and redeemed
        for free entries, prizes, and the planting of trees. Tokens allow you to
        enter the draws and raffles in Token Town and are valid for 12 months,
        so you can save them up for some epic prizes too.
      </p>
    </>,
  ],
];

const goodToKnow: [emoji: string, title: string, text: ReactNode][] = [
  [
    '🗓️',
    'Draw days',
    <p>
      We draw the winning numbers every Tuesday & Friday at 5pm. Watch live
      Fridays on{' '}
      <a target="_blank" href="https://www.instagram.com/daymadeuk/">
        Instagram
      </a>
    </p>,
  ],

  [
    '✉️',
    'If you win',
    <p>
      We post the winning numbers on our{' '}
      <Link href="/results">Results page</Link> and email all winners directly
      after the draws.
    </p>,
  ],

  [
    '🌳',
    'Plant trees',
    <p>
      Every purchase on our site plants a tree with our partner Veritree.{' '}
      <Link href="/giving-back">See how</Link>
    </p>,
  ],

  [
    '👾',
    'Tokens',
    <p>
      You can collect tokens to exchange for prizes, free entries and more in{' '}
      <Link href="/token-town">Token Town</Link>
    </p>,
  ],
];

const HowItWorks = () => {
  return (
    <MainLayout additionalClassName="howItWorks" limitItemWidths>
      <NextSeo
        title="Online Competition | Win Holidays in Our Competitions | DAYMADE"
        description="Win unforgettable holidays with DAYMADE's online competition. Prizes for our holiday competitions include luxury breaks and epic adventures. Join the competition here."
        openGraph={{
          title:
            'Online Competition | Win Holidays in Our Competitions | DAYMADE',
          description:
            "Win unforgettable holidays with DAYMADE's online competition. Prizes for our holiday competitions include luxury breaks and epic adventures. Join the competition here.",
        }}
      />
      <div className="aboutUsTypographyReset">
        <PageTitle
          heroBackgroundImage={`${ASSET_PREFIX}/images/how-it-works/hero-background.svg`}
          rebrandDm2025
        >
          How it works
        </PageTitle>

        <AboutUsModule background="white">
          <AboutUsSiteContainer>
            <EasySteps />
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="grey">
          <AboutUsSiteContainer withVerticalGaps>
            <h2
              className="aboutUsTypographyHeaderXlargeOnWhite"
              data-text="What prizes can I win?"
            >
              What prizes can I win?
            </h2>
            <TabControl
              pages={[
                {
                  title: 'Premium',
                  content: <PrizeList planType="PREMIUM" />,
                },
                {
                  title: 'Lite',
                  content: <PrizeList planType="LITE" />,
                },
                {
                  title: 'Postal',
                  content: <PrizeList planType="POSTAL" />,
                },
              ]}
            />
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="tealGradient">
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlarge">Good to know</h2>
            <EmblaCarousel
              className={styles.goodToKnowCarousel}
              items={goodToKnow.map((x, index) => (
                <div
                  key={index}
                  className={'withReducedSpacing ' + styles.goodToKnowCard}
                >
                  <h3 className="aboutUsTypographyHeaderMedium">
                    <span>{x[0]}</span> {x[1]}
                  </h3>
                  <p>{x[2]}</p>
                </div>
              ))}
            />

            <div className={styles.goodToKnowFaqs}>
              {faqs.map((x, index) => (
                <FaqCard
                  key={index}
                  rebrand2025
                  question={x[0]}
                  answer={x[1]}
                ></FaqCard>
              ))}
              <p className={styles.centered}>
                <a
                  target="_blank"
                  href="https://daymade.zendesk.com/hc/en-gb/requests/new?ticket_form_id=14827697795729&tf_subject=FAQ%20feedback"
                >
                  Any other questions, contact us here
                </a>
              </p>
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>
      </div>
    </MainLayout>
  );
};

interface PrizeListProps {
  planType: keyof typeof prizeTableByPlanType;
}
const PrizeList = (props: PrizeListProps) => {
  const [showMore, setShowMore] = useState(false);
  const prizesForPlanType = prizeTableByPlanType[props.planType];
  const [prizeTableEntryForModal, setPrizeTableEntryForModal] = useState<
    undefined | IPrizeTableEntry
  >();

  if (props.planType == 'POSTAL') {
    return (
      <div className={`${styles.prizeList}`}>
        <div className={styles.postalInfo}>
          <p>
            Postal entrants can win the same prizes, with the same chances of
            winning, as our Premium entrants.
          </p>
          <p className={styles.buttonContainer}>
            <RoundedButton
              href="/alternative-entry-method"
              colorStyle={ColorStyle.OutlineDark}
            >
              Find out more
            </RoundedButton>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.prizeList} ${showMore ? styles.withExtraRows : ''}`}
    >
      <ul>
        {prizesForPlanType.prizes.map((x, index) => (
          <li
            className={`${index >= prizesForPlanType.initialPrizeCountToShow ? styles.extraRow : ''} ${index === 0 ? styles.topRow : ''}`}
          >
            <div className={styles.prizeRowInner}>
              {x.prizeTier.hasPrizePageLink && (
                <Link href={`/prizes?tier=${x.prizeTier.key}`}>
                  <a>{x.prizeTier.titleWithSuffix}</a>
                </Link>
              )}
              {index === 0 && (
                <div
                  className={
                    styles.examplePrizeImage +
                    ' ' +
                    styles.examplePrizeImageBigger
                  }
                  style={{
                    ['--how-it-works-image-filename-mobile' as any]: `url(${ASSET_PREFIX}/images/how-it-works/2025/mobile/${x.prizeTier.useGenericImage ? 'Lower' : x.prizeTier.key}.jpg)`,
                    ['--how-it-works-image-filename-desktop' as any]: `url(${ASSET_PREFIX}/images/how-it-works/2025/desktop/${x.prizeTier.useGenericImage ? 'Lower' : x.prizeTier.key}.jpg)`,
                  }}
                />
              )}
              <div className={styles.prizeListTier}>
                <img
                  className={styles.prizeListBadge}
                  src={x.prizeTier.badge}
                />
                {index !== 0 && (
                  <strong className={styles.prizeTierTitle}>
                    {typeof x.prizeTier.titleWithSuffix == 'string'
                      ? spaceToLineBreak(x.prizeTier.titleWithSuffix)
                      : x.prizeTier.titleWithSuffix}
                  </strong>
                )}
                {index !== 0 && (
                  <div
                    className={styles.examplePrizeImage}
                    style={{
                      ['--how-it-works-image-filename-mobile' as any]: `url(${ASSET_PREFIX}/images/how-it-works/2025/mobile/${x.prizeTier.useGenericImage ? 'Lower' : x.prizeTier.key}.jpg)`,
                      ['--how-it-works-image-filename-desktop' as any]: `url(${ASSET_PREFIX}/images/how-it-works/2025/desktop/${x.prizeTier.useGenericImage ? 'Lower' : x.prizeTier.key}.jpg)`,
                    }}
                  />
                )}
              </div>
              <div className={styles.prizeListInfo}>
                {index === 0 && (
                  <strong className={styles.prizeTierTitle}>
                    {x.prizeTier.titleWithSuffix}
                  </strong>
                )}
                <div className={styles.prizeListWorth}>
                  {x.prizeTier.alternativePrizeInfoHeading ?? 'Worth up to'}{' '}
                  <strong>
                    {x.prizeTier.alternativePrizeInfoText ??
                      `£${x.prizeTier.value.toLocaleString('en-GB')}`}
                  </strong>
                </div>
                <div className={styles.prizeListMatches}>
                  <img
                    width="20"
                    height="20"
                    className={styles.prizeListMatchesBalls}
                    src={`${ASSET_PREFIX}/images/how-it-works/balls.png`}
                    alt=""
                  />
                  {getMatchText(x)}
                </div>
              </div>
              {x.prizeTier.hasPrizePageLink && (
                <div className={styles.prizeListArrow}>
                  <Arrow10x20 />
                </div>
              )}
              {x.prizeTier.modalHeading && (
                <button
                  className={styles.infoButton}
                  aria-label="Open more information"
                  onClick={() => setPrizeTableEntryForModal(x)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.7369 0.615967C4.11433 0.615967 0.353516 4.37678 0.353516 8.99935C0.353516 13.6219 4.11433 17.3827 8.7369 17.3827C13.3595 17.3827 17.1203 13.6219 17.1203 8.99935C17.1203 4.37678 13.3595 0.615967 8.7369 0.615967ZM8.7369 1.87347C12.6799 1.87347 15.8628 5.05639 15.8628 8.99935C15.8628 12.9423 12.6799 16.1252 8.7369 16.1252C4.79394 16.1252 1.61102 12.9423 1.61102 8.99935C1.61102 5.05639 4.79394 1.87347 8.7369 1.87347ZM8.7369 4.80766C8.51456 4.80766 8.30132 4.89598 8.1441 5.0532C7.98689 5.21042 7.89856 5.42366 7.89856 5.646C7.89856 5.86834 7.98689 6.08157 8.1441 6.23879C8.30132 6.39601 8.51456 6.48434 8.7369 6.48434C8.95924 6.48434 9.17247 6.39601 9.32969 6.23879C9.48691 6.08157 9.57524 5.86834 9.57524 5.646C9.57524 5.42366 9.48691 5.21042 9.32969 5.0532C9.17247 4.89598 8.95924 4.80766 8.7369 4.80766ZM8.72707 7.73284C8.56046 7.73544 8.40169 7.80407 8.28565 7.92365C8.1696 8.04323 8.10576 8.20398 8.10815 8.3706V12.9815C8.10697 13.0648 8.12236 13.1475 8.15343 13.2248C8.1845 13.3021 8.23062 13.3725 8.28913 13.4318C8.34763 13.4912 8.41734 13.5383 8.49421 13.5704C8.57108 13.6026 8.65357 13.6192 8.7369 13.6192C8.82022 13.6192 8.90272 13.6026 8.97959 13.5704C9.05646 13.5383 9.12617 13.4912 9.18467 13.4318C9.24317 13.3725 9.2893 13.3021 9.32037 13.2248C9.35144 13.1475 9.36683 13.0648 9.36565 12.9815V8.3706C9.36686 8.28644 9.35116 8.2029 9.31949 8.12493C9.28781 8.04695 9.24081 7.97613 9.18126 7.91665C9.12171 7.85718 9.05082 7.81026 8.97281 7.77869C8.89479 7.74711 8.81123 7.73152 8.72707 7.73284Z"
                      fill="#4A4A4A"
                    />
                  </svg>
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {prizeTableEntryForModal && (
        <GenericModal
          onClose={() => setPrizeTableEntryForModal(undefined)}
          ariaLabelledByElementId="prizeTierModalHeadingId"
        >
          <img
            src={prizeTableEntryForModal.prizeTier.badge}
            width={50}
            height={50}
            alt="Prize tier badge"
          />
          <h1 id="prizeTierModalHeadingId" className={styles.modalHeading}>
            {prizeTableEntryForModal.prizeTier.modalHeading}
          </h1>
          <p>{prizeTableEntryForModal.prizeTier.modalText}</p>
          <div className={styles.matchBanner}>
            <img
              width="20"
              height="20"
              className={styles.prizeListMatchesBalls}
              src={`${ASSET_PREFIX}/images/how-it-works/balls.png`}
              alt=""
            />
            {getMatchText(prizeTableEntryForModal)}
          </div>
        </GenericModal>
      )}

      <button
        className={styles.showMore}
        onClick={() => setShowMore((x) => !x)}
      >
        See {showMore ? 'less' : 'more'}
      </button>
      <div className={styles.buttonContainer}>
        <RoundedButton rebrandDm2025 href="/choose-your-plan">
          Enter now
        </RoundedButton>
      </div>
    </div>
  );
};

function spaceToLineBreak(text: string) {
  return (
    <>
      {text.split(' ').map((x, index) => (
        <Fragment key={index}>
          {index > 0 ? <br /> : undefined}
          {x}
        </Fragment>
      ))}
    </>
  );
}

export default HowItWorks;
